import { FC, ReactNode, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useServices } from "../../services/Services";
import ErrorState from "../ErrorState/ErrorState";
import { useDatadogUser } from "../../hooks/useDatadogUser";

interface WithActiveSubscriptionProps {
  children?: ReactNode;
}

const WithActiveSubscription: FC<WithActiveSubscriptionProps> = function ({
  children,
}) {
  const services = useServices();
  const userQuery = services.User.useGetCurrent();
  const {
    data: user,
    isSuccess: userLoaded,
    isError: userFailed,
    error: userError,
  } = userQuery;
  const hasBypass = !!user?.paymentBypassReason;
  const { data: subscription, isSuccess: subscriptionLoaded } =
    services.User.useGetStripeDetails(hasBypass ? undefined : user?.email);
  const navigate = useNavigate();

  useDatadogUser(user);

  useEffect(() => {
    // Skip Stripe subscription check for payment bypass users
    if (
      !hasBypass &&
      subscriptionLoaded &&
      (!subscription || !subscription.inGoodStanding)
    ) {
      navigate("/subscription-expired");
      return;
    }
    // User status check applies to all users including bypass
    const isReady = hasBypass ? userLoaded : subscriptionLoaded && userLoaded;
    if (isReady && (!user || user.status !== "Active")) {
      navigate("/inactive-user");
      return;
    }
  }, [navigate, subscription, user, userLoaded, subscriptionLoaded, hasBypass]);

  // Without this the gate falls through to the app shell, where every query is
  // still blocked on the user — an app that looks like it is loading forever.
  if (userFailed) {
    return <ErrorState error={userError} onRetry={() => userQuery.refetch()} />;
  }

  return children ?? <Outlet />;
};

export default WithActiveSubscription;
